/* eslint-disable @ok/oklint/no-hardcode-variable */
import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { linkTo } from '@ok/blade';

import './index.less';
import { CompanyCard } from './CompanyCard';
import { fetchCompanies, fetchKycPassed } from './api';
import SbeModal from './components/SbeModal';
import { Company } from './types';
import {
  getCompanyDest,
  getLoginUrl,
  KYC_URL,
  getBgTopImageUrl,
  getBgBottomImageUrl,
  getOkjLogoUrl,
} from './utils';

const BgTopImage: React.FC = () => (
  <img
    className="sbe-bg-top"
    src={getBgTopImageUrl()}
    aria-hidden="true"
    alt=""
  />
);

const BgBottomImage: React.FC = () => (
  <img
    className="sbe-bg-bottom"
    src={getBgBottomImageUrl()}
    aria-hidden="true"
    alt=""
  />
);

const HeroHeader: React.FC = () => (
  <div className="sbe-hero-header" data-testid="sbe-hero-header">
    <img className="sbe-okj-logo" src={getOkjLogoUrl()} alt="OKJ" />
    <h1 className="sbe-title">株主優待お申込みページ</h1>
  </div>
);

const Description: React.FC = () => (
  <p className="sbe-description" data-testid="sbe-description">
    以下よりエントリーを行う企業をお選びいただき、株主優待のエントリー申請へお進みください。
  </p>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ShareholderBenefitEntry: React.FC = () => {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [loginModalForCompany, setLoginModalForCompany] =
    useState<Company | null>(null);
  const [kycModalOpen, setKycModalOpen] = useState(false);

  const setupCompanies = async () => {
    try {
      const result = await fetchCompanies();
      setCompanies(result);
    } catch {
      // Error toast is handled by @common/utils/dataProxy.
    }
  };

  useEffect(() => {
    setupCompanies();
  }, []);

  const handleCardClick = async (company: Company) => {
    if (company.landingPageUrl) {
      linkTo(company.landingPageUrl);
      return;
    }

    if (!window.utils?.ont?.isLogin()) {
      setLoginModalForCompany(company);
      return;
    }
    try {
      const kycPassed = await fetchKycPassed();
      if (!kycPassed) {
        setKycModalOpen(true);
        return;
      }
    } catch {
      // Error toast is handled by @common/utils/dataProxy.
      return;
    }
    const dest = getCompanyDest(company);
    linkTo(dest);
  };

  let companyListContent: React.ReactNode = null;
  if (companies !== null && companies.length === 0) {
    companyListContent = (
      <p className="sbe-empty-state" data-testid="sbe-empty-state">
        現在、お申込み受付中の株主優待はございません。
      </p>
    );
  } else {
    companyListContent = companies?.map((company) => (
      <CompanyCard
        key={company.id}
        company={company}
        onClick={handleCardClick}
      />
    ));
  }

  return (
    <div
      className="shareholder-benefit-entry"
      data-testid="shareholder-benefit-entry"
    >
      <BgTopImage />
      <BgBottomImage />
      <HeroHeader />
      <Description />

      <section className="sbe-company-list" data-testid="sbe-company-list">
        {companyListContent}
      </section>

      {loginModalForCompany && (
        <SbeModal
          titleId="sbe-login-modal-title"
          title="ログインが必要です"
          body="株主優待のお申し込みには、OKJへのログインが必要です。"
          ctaHref={getLoginUrl(loginModalForCompany)}
          ctaLabel="ログインする"
          onClose={() => setLoginModalForCompany(null)}
        />
      )}
      {kycModalOpen && (
        <SbeModal
          titleId="sbe-kyc-modal-title"
          title="本人確認が完了していません"
          body="株主優待のお申し込みには、OKJでの本人確認完了が必要です。"
          ctaHref={KYC_URL}
          ctaLabel="本人確認へ進む"
          onClose={() => setKycModalOpen(false)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const container = document.getElementById('root') || document.body;
createRoot(container).render(<ShareholderBenefitEntry />);
